import type { ReactNode } from 'react';
import type { FulfillmentCarrierVoidRecord } from '@/infrastructure/FulfillmentAdminClient';
import {
  CARRIER_VOID_STATE_CHIP,
  CARRIER_VOID_STATE_LABEL,
  carrierParcelOf,
  carrierVoidRetryable,
} from '@/utils/fulfillmentCarrierVoid';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { RefreshIcon, TruckIcon } from '@/pages/fulfillment/components/UltimateIcons';

interface CarrierCancelBlockProps {
  /** The latest carrier cancel the order's event stream recorded (SSD-7459). */
  readonly record: FulfillmentCarrierVoidRecord;
  readonly busy: boolean;
  readonly onRetry: () => void;
  /** Anything the screen adds under the record, such as where the order stands on Manual carrier cancels. */
  readonly children?: ReactNode;
}

/**
 * Where a cancelled or closed marketing order's carrier booking ended (SSD-7459): cancelled at the carrier, nothing to
 * cancel, failed, or still owed. A cancel still owed or one a second try can clear offers Retry carrier cancel; one the
 * carrier refused for good says it has to be cancelled by hand.
 */
export function CarrierCancelBlock({ record, busy, onRetry, children }: CarrierCancelBlockProps) {
  const retryable = carrierVoidRetryable(record);
  const refusedForGood = record.state === 'FAILED' && !record.retryable;
  const when = record.recordedAt ? kstTimestamp(record.recordedAt) : null;
  return (
    <div className="cvoid" data-testid="carrier-void">
      <div className="cvoid-head">
        <TruckIcon />
        <span className="cvoid-title">Carrier cancel</span>
        {/* A state this screen does not know yet is shown as the server wrote it, never as a blank chip. */}
        <span className={`status ${CARRIER_VOID_STATE_CHIP[record.state] ?? ''}`}>
          {CARRIER_VOID_STATE_LABEL[record.state] ?? record.state}
        </span>
        {retryable && (
          <button type="button" className="btn btn-sm btn-line cvoid-retry" disabled={busy} onClick={onRetry}>
            <RefreshIcon />
            Retry carrier cancel
          </button>
        )}
      </div>
      <div className="cvoid-body">
        <span className="cvoid-parcel">{carrierParcelOf(record)}</span>
        {record.code && <span className="cvoid-code">{record.code}</span>}
        {when && (
          <span className="cvoid-when">
            {when}
            {record.actor ? ` · ${record.actor}` : ''}
          </span>
        )}
      </div>
      {record.message && <div className="cvoid-message">{record.message}</div>}
      {record.state === 'NOT_ATTEMPTED' && (
        <div className="blocked-note">
          The close owes this cancel and it has not run yet: it may still be queued, or it was lost when the server
          restarted.
        </div>
      )}
      {refusedForGood && (
        <div className="blocked-note cvoid-manual">
          The carrier will not cancel it: cancel it by hand at the carrier.
        </div>
      )}
      {children}
    </div>
  );
}
